import { FirebaseApp, initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';

/**
 * True when the app is running on a developer machine against the local
 * functions emulator - the only case in which the client should be talking to
 * emulators rather than to the real dev project.
 */
const isLocal = () =>
  window.location.hostname === 'localhost' &&
  !!process.env.REACT_APP_BACKEND_URL?.includes('localhost');

/**
 * The Firebase app every web app signs in and reads through.
 *
 * All eight apps declare the same `REACT_APP_FIREBASE_*` config and want the
 * same emulator behaviour, so this is one implementation rather than eight
 * copies that drift - which they had: the former admin app connected the
 * Firestore emulator and the former expert app did not, so one read the
 * emulator while the other read the real dev database.
 *
 * Both emulator connections can be opted out of individually
 * (REACT_APP_USE_FIRESTORE_EMULATOR / REACT_APP_USE_AUTH_EMULATOR = false),
 * which is what `dev:remote` needs: the functions emulator running against the
 * real dev project.
 */
export const createFirebaseApp = (): FirebaseApp => {
  const firebaseApp = initializeApp({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  });

  if (!isLocal()) {
    return firebaseApp;
  }

  // The client must read the SAME Firestore the functions emulator writes to,
  // otherwise saves land in the emulator while the UI reads the real dev
  // database and never sees them.
  if (process.env.REACT_APP_USE_FIRESTORE_EMULATOR !== 'false') {
    connectFirestoreEmulator(getFirestore(firebaseApp), '127.0.0.1', 8080);
  }

  // `createCustomToken`, which the Descope sign-in exchange ends in, signs with
  // the runtime service account through the GCE metadata server, and a
  // developer machine has none. Sign-in can only complete against the Auth
  // emulator, where the token is signed by the emulated signer instead.
  if (process.env.REACT_APP_USE_AUTH_EMULATOR !== 'false') {
    connectAuthEmulator(getAuth(firebaseApp), 'http://127.0.0.1:9099', {
      disableWarnings: true,
    });
  }

  return firebaseApp;
};
